import { useEffect } from 'react'

interface UseBeforeUnloadOptions {
  enabled?: boolean
  message?: string
}

/**
 * Custom hook to show a warning when the user tries to close the tab/window
 * @param options - Configuration options for the beforeunload behavior
 */
export const useBeforeUnload = (options: UseBeforeUnloadOptions = {}) => {
  const { enabled = true, message = 'Are you sure you want to leave? You may lose unsaved changes.' } = options

  useEffect(() => {
    if (!enabled) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Allow bypassing the prompt dynamically (e.g., when kicked by duplicate login)
      if (typeof window !== 'undefined' && (window as any).bypassBeforeUnload) {
        return
      }

      // Show a confirmation dialog
      event.preventDefault()
      
      // Set the return value to show the browser's default message
      // Note: Modern browsers don't allow custom messages for security reasons
      event.returnValue = message
      
      return message
    }

    // Add the event listener
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [enabled, message])
}

export default useBeforeUnload 